import { Record as record } from 'immutable';
import _ from 'lodash';

import { sum } from '../helpers/Immutable';
import { construct, resolve, fields } from '../helpers/LinkMapper';
import { getDefaults, getResolvers } from '../helpers/Records';
import Form from '../records/payment/Form';
import AuthRequest from '../records/payment/AuthRequest';
import AuthResponse from '../records/payment/AuthResponse';
import FeatureFlagStore from './FeatureFlagStore';
import OrderStore from './OrderStore';
import BaseStore from './BaseStore';
import { ActionTypes, PaymentType } from '../constants/AppConstants';
import { PaymentUnauthorizedError } from '../errors/Payment';

export const getCompatibleCardPaymentMethod = ({
    currentPaymentMethod,
    featurePay360ApiEnabled,
    featurePay360HostedEnabled,
    featureBraintreeCardPaymentsEnabled
}) => {
    // Return the current payment method if it's not a card-related payment type
    if (
        currentPaymentMethod !== PaymentType.CARD &&
        currentPaymentMethod !== PaymentType.PAY360_HOSTED
    ) {
        return currentPaymentMethod;
    }

    // If Braintree is enabled, prioritize it
    if (featureBraintreeCardPaymentsEnabled) {
        return PaymentType.BRAINTREE_CARD_CHECKOUT;
    }

    // Handle CARD method with Pay360 API and hosted feature flags
    if (currentPaymentMethod === PaymentType.CARD) {
        if (!featurePay360ApiEnabled && featurePay360HostedEnabled) {
            return PaymentType.PAY360_HOSTED;
        }
        return PaymentType.CARD;
    }

    // Handle Pay360 Hosted method
    if (currentPaymentMethod === PaymentType.PAY360_HOSTED && !featurePay360HostedEnabled) {
        return PaymentType.CARD;
    }

    // Default to Pay360 Hosted if no conditions match
    return PaymentType.PAY360_HOSTED;
};

const stateSchema = {
    form: [ resolve.as(Form) ],
    isProcessing: [fields.bool, false],
    is3DSecure: [fields.bool, false],
    authRequest: [resolve.as(AuthRequest)],
    authResponse: [resolve.as(AuthResponse)],
    authError: [fields.identity],
    errorMessage: [fields.identity],
    waitMessage: [fields.string],
    vouchersPreAuthedAmount: [fields.number, 0],
};
const defaultState = getDefaults(stateSchema);

export class PaymentState extends record(defaultState) {
    static fromJSON(json = {}) {
        return construct(PaymentState, _.defaults(json, defaultState), getResolvers(stateSchema));
    }
}

class PaymentStore extends BaseStore {

    resetState() {
        // https://ontrackretail.slack.com/archives/C03KCFHMX/p1593180469005100
        this.setState(new PaymentState());
    }

    serialize() {
        // strip sensitive/unnecessary stuff
        return _.omit(super.serialize(), 'authRequest', 'authResponse');
    }

    getActionHandlers () {
        return {
            [ ActionTypes.PAYMENT_FORM_CHANGED ]: this.handlePaymentFormChanged,
            [ ActionTypes.PAYMENT_FORM_ERROR_MESSAGE ]: this.handlePaymentFormErrorMessage,
            [ ActionTypes.PAYMENT_DISCOUNT_CODE_CHANGED ]: this.handlePaymentDiscountCodeChanged,
            [ ActionTypes.PAYMENT_AUTH_STARTED ]: this.handlePaymentAuthStarted,
            [ ActionTypes.PAYMENT_AUTH_FINISHED ]: this.handlePaymentAuthFinished,
            [ ActionTypes.PAYMENT_AUTH_FAILED ]: this.handlePaymentAuthFailed,
            [ ActionTypes.PAYMENT_AUTH_RESET ]: this.handlePaymentAuthReset,
            [ ActionTypes.PAYMENT_AUTH_WAITING ]: this.handlePaymentWaiting,
            [ ActionTypes.PAYMENT_SETTLEMENT_STARTED ]: this.handlePaymentSettlementStarted,
            [ ActionTypes.ORDER_REFRESH_LOADED ]: this.handleOrderRefreshLoaded,
            [ ActionTypes.ORDER_REFRESH_FAILED ]: this.handleOrderRefreshFailed,
            [ ActionTypes.PAYMENT_LEFT ]: this.handlePaymentLeft,
            [ ActionTypes.ORDER_PAID ]: this.resetForm,
            [ ActionTypes.BASKET_CLEARED ]: this.resetState,
            [ ActionTypes.ORDER_EXPIRED ]: this.resetState,
            [ ActionTypes.SEARCH_STARTED ]: this.resetState,
            [ ActionTypes.SEASON_SEARCH_STARTED ]: this.resetState,
            [ ActionTypes.DAYSAVE_SEARCH_STARTED ]: this.resetState,
            [ ActionTypes.ROVERSRANGERS_SEARCH_STARTED ]: this.resetState,
            [ ActionTypes.CUSTOMER_VOUCHERS_LOADED ]: this.handleVouchersLoaded,
            [ ActionTypes.ORDER_UPDATED ]: this.handleOrderUpdated,
            [ ActionTypes.ORDER_TRIP_CREATED ]: this.handleOrderUpdated,
            [ ActionTypes.ORDER_TRIP_UPDATED ]: this.handleOrderUpdated,
            [ ActionTypes.ORDER_TRIP_DELETED ]: this.handleOrderUpdated,
            [ ActionTypes.PAYMENT_PRE_AUTH_VOUCHERS_RELEASED ]: this.handleVoucherPreAuthRelease,
            [ ActionTypes.PAYMENT_PRE_AUTH_VOUCHERS_COMPLETED ]: this.handleVoucherPreAuthComplete,
            [ ActionTypes.LOGGED_OUT ]: this.resetForm,
            [ ActionTypes.LOGGED_IN ]: this.handleLogin,
            [ ActionTypes.APPLY_DISCOUNT_CODE_FINISHED]: this.handleDiscountApplied,
            [ ActionTypes.APPLY_VOUCHER_CODE_FINISHED ]: this.handleVoucherCodeApplied,
            [ ActionTypes.REMOVE_VOUCHER_CODE_FINISHED ]: this.handleOrderUpdated,

        };
    }

    handleLogin ({ user }) {
        const { form } = this.state;
        if (form) {
            this.setState(
                this.state.set(
                    'form',
                    form.set('isLoggedIn', true).setContactDetails(user.email, user.phone),
                ),
            );
        }
    }

    handleVouchersLoaded ({ vouchers }) {
        if (!FeatureFlagStore.get('enableEVoucherCheckout')) {
            return;
        }

        const total = sum(vouchers, 'amount');
        const { form, vouchersPreAuthedAmount } = this.state;
        if (!form) {
            return;
        }

        let updatedForm = form;
        if (form.getVouchersTotal() !== total + vouchersPreAuthedAmount) {
            updatedForm = updatedForm.setVouchersTotal(total, vouchersPreAuthedAmount);
        }
        if (OrderStore.getAppliedVoucher() && updatedForm.vouchers.useVouchers) {
            updatedForm = updatedForm.setIn(['vouchers', 'useVouchers'], false);
        }

        if (updatedForm !== form) {
            this.setState(this.state.set('form', updatedForm));
        }
    }

    handleOrderUpdated ({ order }) {
        const appliedVoucher = OrderStore.getAppliedVoucher();
        const basketTotal = Math.max(0, order.totalPrice - (appliedVoucher ? appliedVoucher.amount : 0));
        const { form } = this.state;
        if (form && form.basketTotal !== basketTotal) {
            this.setState(this.state.set('form', form.setBasketTotal(basketTotal)));
        }
    }

    handleDiscountApplied ({ updatedOrder }) {
        this.handleOrderUpdated({ order: updatedOrder });
    }

    handleVoucherCodeApplied ({ order }) {
        this.handleOrderUpdated({ order });
        const { form } = this.state;
        if (form && form.getVoucherAmount() > 0) {
            this.setState(this.state.setIn(['form', 'vouchers', 'useVouchers'], false));
        }
    }

    handlePaymentFormErrorMessage({ errorMessage }) {
        this.setState(this.state.set('errorMessage', errorMessage));
    }

    handlePaymentFormChanged ({ form }) {
        this.setState(this.state.set('form', form));
    }

    handlePaymentDiscountCodeChanged ({ discountCode }) {
        this.setState(this.state.setIn(['form', 'discountCode'], discountCode));
    }

    handlePaymentAuthStarted () {
        const updatedState = this.state.withMutations((state) => {
            return state
                .set('isProcessing', true)
                .set('is3DSecure', false)
                .set('authRequest', null)
                .set('authResponse', null)
                .set('authError', null);
        });
        this.setState(updatedState);
    }

    handlePaymentAuthFinished ({ authRequest, authResponse }) {
        const updatedState = this.state.withMutations((state) => {
            return state
                .set('isProcessing', authResponse.isEnrolled())
                .set('is3DSecure', authResponse.isEnrolled())
                .set('authRequest', authRequest)
                .set('authResponse', authResponse)
                .set('authError', null);
        });
        this.setState(updatedState);
    }

    handlePaymentAuthFailed ({ authRequest, authError }) {
        const updatedState = this.state.withMutations((state) => {
            return state
                .set('isProcessing', false)
                .set('is3DSecure', false)
                .set('authRequest', authRequest)
                .set('authResponse', null)
                .set('authError', authError)
                .set('waitMessage', null);
        });
        this.setState(updatedState);
    }

    handlePaymentLeft() {
        this.resetState();
    }

    handlePaymentWaiting ({ message }) {
        this.setState(this.state.set('waitMessage', message));
    }

    handlePaymentSettlementStarted () {
        const updatedState = this.state.withMutations((state) => {
            return state
                .set('isProcessing', true)
                .set('is3DSecure', false)
                .set('authError', null)
                .set('waitMessage', null);
        });
        this.setState(updatedState);
    }

    handleOrderRefreshLoaded ({ order, isSettling = false }) {
        /*
            A settling poll is waiting for the backend to confirm a payment that has already been
            taken, and refresh() only reaches here while the order is still unpaid — it dispatches
            ORDER_PAID once it lands. So "not paid yet" is not a decline: keep the loader up and
            raise no error. It is released when the customer leaves the page (PAYMENT_LEFT) or when
            the settle loop gives up and resets.
        */
        const updatedState = this.state.withMutations((state) => {
            return state
                .set('isProcessing', isSettling)
                .set('is3DSecure', false)
                .set('authError', isSettling || order.isPaid() ? null : new PaymentUnauthorizedError(order.state))
                .set('waitMessage', null);
        });
        this.setState(updatedState);
    }

    handleOrderRefreshFailed ({ error, isSettling = false }) {
        // A poll that fails mid-settle is a transient error against an already-paid order, and the
        // loop still has retries left, so keep waiting instead of declining the payment.
        const updatedState = this.state.withMutations((state) => {
            return state
                .set('isProcessing', isSettling)
                .set('is3DSecure', false)
                .set('authError', isSettling ? null : new PaymentUnauthorizedError(error.message))
                .set('waitMessage', null);
        });
        this.setState(updatedState);
    }

    handlePaymentAuthReset () {
        const updatedState = this.state.withMutations((state) => {
            return state
                .set('isProcessing', false)
                .set('is3DSecure', false)
                .set('authRequest', null)
                .set('authResponse', null)
                .set('authError', null)
                .set('waitMessage', null);
        });
        this.setState(updatedState);
    }

    handleVoucherPreAuthRelease() {
        this.setState(this.state.set('vouchersPreAuthedAmount', 0));
    }

    handleVoucherPreAuthComplete({amount}) {
        this.setState(this.state.set('vouchersPreAuthedAmount', amount));
    }

    resetForm () {
        const updatedState = this.state.set('form', defaultState.form);
        this.setState(updatedState);
    }

    getForm () {
        return this.get('form');
    }

    getValidation () {
        return this.get('validation');
    }

    isProcessing () {
        return this.get('isProcessing');
    }

    is3DSecure () {
        return this.get('is3DSecure');
    }

    getAuthRequest () {
        return this.get('authRequest');
    }

    getAuthResponse () {
        return this.get('authResponse');
    }

    getAuthError () {
        return this.get('authError');
    }

    getPaymentAmount () {
        if (!this.state.form) {
            return 0;
        }
        return this.state.form.getPaymentAmount();
    }
}


const storeInstance = new PaymentStore({
    storageKey: 'payment',
    expires: 30 * 60,
    recordType: PaymentState,
});
export default storeInstance;
